import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Typography, Timeline, Card } from 'antd';
import dayjs from '../../../dayjs';
import {ReactComponent as Calendar} from '../../../assets/icons/Calendar.svg';
import {ReactComponent as Services} from '../../../assets/icons/Services.svg';
import {ReactComponent as User} from '../../../assets/icons/User_s.svg';
import {ReactComponent as Timer} from '../../../assets/icons/Timer.svg';
import DeckGL from '@deck.gl/react';
import { StaticMap } from 'react-map-gl';
import { GeoJsonLayer } from '@deck.gl/layers';
import { PathStyleExtension } from '@deck.gl/extensions';
import config from '../../../config';
import { getViewport } from '../../../libs/mapLib';
import 'mapbox-gl/dist/mapbox-gl.css';
import { createRouteFromItinerary } from '../ExpressBooking';
import { last } from 'lodash';
import { ScatterplotLayer } from '@deck.gl/layers';
import { TextLayer } from '@deck.gl/layers';
import { LngLatBounds } from 'mapbox-gl';

const { Title, Text } = Typography;

const MAPBOX_ACCESS_TOKEN = config.maps.mabBox;
const MAPBOX_STYLE = 'mapbox://styles/mapbox/light-v9';

const INITIAL_VIEW_STATE = {
    longitude: 147.638367,
    latitude: -32.32370,
    zoom: 5,
    pitch: 0,
    bearing: 0
};

const ReviewStep = ({ data }) => {
    const { itinerary, vehicles, contact } = data;
    const [route, setRoute] = useState(null);
    const [stops, setStops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
    const containerRef = useRef(null);

    const formatTime = (date, time) => {
        if (!date || !time) return '';
        const dateTime = dayjs(date).hour(time.getHours()).minute(time.getMinutes());
        return dateTime.format('h:mm A');
    };

    // Create route from itinerary data
    useEffect(() => {
        const generateRoute = async () => {
            try {
                setLoading(true);
                // Use the imported createRouteFromItinerary function
                const { route: newRoute, stops: routeStops } = await createRouteFromItinerary(data);

                setRoute(newRoute);
                setStops(routeStops);
                setLoading(false);
            } catch (error) {
                console.error('Error creating route:', error);
                setLoading(false);
            }
        };

        if (itinerary && itinerary.stops && itinerary.stops.length > 0) {
            generateRoute();
        }
    }, [data]);

    // Auto-fit the view to show all points
    useEffect(() => {
        if (!route || !route.waypoints || route.waypoints.length === 0 || !containerRef.current) return;

        const container = containerRef.current;
        const { width, height } = container.getBoundingClientRect();

        // Create a bounds object
        const bounds = new LngLatBounds();
        
        // Add all waypoints to the bounds
        route.waypoints.forEach(wp => {
            bounds.extend([wp.lon, wp.lat]);
        });
        
        // Add all stops to the bounds
        stops.forEach(stop => {
            bounds.extend([stop.lon, stop.lat]);
        });
        
        // Get the center and zoom level that fits the bounds
        const center = bounds.getCenter();
        
        // Calculate the zoom level with a more appropriate formula
        // and add padding to ensure the route is fully visible
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        
        // Calculate the width and height of the bounds in degrees
        const widthDegrees = ne.lng - sw.lng;
        const heightDegrees = ne.lat - sw.lat;
        
        // Add padding (20% on each side)
        const paddedWidth = widthDegrees * 1.4;
        const paddedHeight = heightDegrees * 1.4;
        
        // Calculate zoom level based on the larger dimension
        const zoomX = Math.log2(360 * width / paddedWidth) - 1;
        const zoomY = Math.log2(180 * height / paddedHeight) - 1;
        
        // Use the smaller zoom level to ensure everything fits
        const zoom = Math.min(
            11, // Max zoom level (reduced from 12)
            Math.max(
                3, // Min zoom level
                Math.min(zoomX, zoomY) - 1 // Subtract 1 to zoom out more
            )
        );
        
        // Set the view state with padding
        setViewState({
            longitude: center.lng,
            latitude: center.lat,
            zoom: zoom,
            pitch: 0,
            bearing: 0,
            transitionDuration: 1000
        });
    }, [route, stops]);

    // Create GeoJSON for route
    const createRouteGeoJson = () => {
        if (!route || !route.waypoints) return null;
        
        return {
            type: 'Feature',
            geometry: {
                type: 'LineString',
                coordinates: route.waypoints.map(wp => [wp.lon, wp.lat])
                    .filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]))
            },
            properties: { color: '#1890ff' }
        };
    };

    // Create GeoJSON for stops
    const createStopsGeoJson = () => {
        if (!stops || stops.length === 0) return null;
        
        return {
            type: 'FeatureCollection',
            features: stops.map((stop, index) => ({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [stop.lon, stop.lat]
                },
                properties: {
                    stopId: stop.stopId,
                    name: stop.stopName,
                    index: index,
                    isFirst: index === 0,
                    isLast: index === stops.length - 1
                }
            }))
        };
    };

    // Create layers
    const layers = [
        // Route layer
        new GeoJsonLayer({
            id: 'route-path',
            data: createRouteGeoJson(),
            pickable: false,
            stroked: false,
            filled: false,
            lineWidthMinPixels: 2,
            parameters: {
                depthTest: false
            },
            getLineColor: [24, 144, 255], // Blue
            getLineWidth: 3,
            extensions: [new PathStyleExtension({ highPrecisionDash: true })]
        }),
        // Stops layer with index added
        new ScatterplotLayer({
            id: 'stops-layer',
            data: stops.map((stop, index) => ({
                ...stop, 
                // Swap lat and lon for Mapbox (weird)
                lat: stop.lon,
                lon: stop.lat,
                index})),
            pickable: true,
            opacity: 1,
            stroked: true,
            filled: true,
            radiusScale: 6,
            radiusMinPixels: 5,
            radiusMaxPixels: 20,
            lineWidthMinPixels: 1,
            getPosition: d => [d.lat, d.lon],
            getFillColor: d => d.index === 0 ? [255, 0, 0] : d.index === stops.length - 1 ? [0, 255, 0] : [255, 255, 0],
            getLineColor: [0, 0, 0],
            getRadius: d => d.index === 0 || d.index === stops.length - 1 ? 10 : 8,
            onClick: info => {
                if (info.object) {
                    console.log('Clicked stop:', info.object);
                }
            }
        }),
        
        // Text layer for stop numbers with index added
        new TextLayer({
            id: 'stop-numbers',
            data: stops.map((stop, index) => ({...stop, index})),
            pickable: false,
            getPosition: d => [d.lon, d.lat],
            // getText: d => d.index === 0 ? 'S' : d.index === stops.length - 1 ? 'D' : (d.index + 1).toString(),
            getSize: 14,
            getAngle: 0,
            getTextAnchor: 'middle',
            getAlignmentBaseline: 'center',
            getPixelOffset: [0, -15],
            getTextColor: [0, 0, 0],
            fontFamily: 'Arial',
            fontWeight: 'bold'
        })
    ];

    return (
        <Card className="card-main map-holder-wrap no-pad d-flex">
            <div 
                ref={containerRef}
                className='map-holder' 
                style={{ 
                    width: '52%', 
                    height: '100%', 
                    background: '#EDEEEE', 
                    position: 'relative', 
                    borderRadius: '8px', 
                    overflow: 'hidden' 
                }}
            >
                {loading ? (
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%' 
                    }}>
                        <Text type="secondary">Loading map...</Text>
                    </div>
                ) : route ? (
                    <DeckGL
                        initialViewState={viewState}
                        controller={true}
                        layers={layers}
                        onViewStateChange={({ viewState }) => setViewState(viewState)}
                    >
                        <StaticMap
                            mapboxApiAccessToken={MAPBOX_ACCESS_TOKEN}
                            mapStyle={MAPBOX_STYLE}
                        />
                    </DeckGL>
                ) : (
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%' 
                    }}>
                        <Text type="secondary">No route data available</Text>
                    </div>
                )}
            </div>
            <div className='map-items' style={{ width: '48%', height: '100%' }}>    
                {/* <Title level={5}>Contact Information</Title>
                <Text strong>Name:</Text> {contact.name}<br />
                <Text strong>Email:</Text> {contact.email}<br />
                <Text strong>Phone:</Text> {contact.phone}<br />
                {contact.company && (
                    <>
                        <Text strong>Company:</Text> {contact.company}<br />
                    </>
                )}
                {contact.specialRequests && (
                    <>
                        <Text strong>Special Requests:</Text> {contact.specialRequests}
                    </>
                )} */}
            
                <Title className='mb-1' level={5} style={{fontWeight: 500}}>One-way trip</Title>
                {route && <div className="d-flex flex-wrap w-100" style={{gap: '0px'}}>
                    <div className="icon-info-text">
                        <Calendar/>
                        <div>
                            <strong>{dayjs().hour(Math.floor(route.stops[0].departSecs / 3600)).minute(Math.floor((route.stops[0].departSecs % 3600) / 60)).format('MMM D, YYYY h:mm A')}</strong>
                        </div>
                    </div>
                    <div className="icon-info-text">
                        <Services/>
                        <div><strong>{Math.round(route?.distance / 1000)}</strong> kms</div>
                    </div>
                    <div className="icon-info-text">
                        <User/>
                        <div><strong>{itinerary.passengers}</strong> passengers</div>
                    </div>
                    <div className="icon-info-text">
                        <Timer/>
                        <div><strong>{Math.floor(route.duration / 3600)}hr {Math.floor((route.duration % 3600) / 60)}m</strong> hours</div> 
                    </div>
                </div>}
                
            
                {/* <Title level={5}>Selected Vehicles</Title>
                <Row gutter={[16, 16]}>
                    {vehicles.map(vehicle => (
                        <Col key={vehicle.vehicleId}>
                            <Card size="small">
                                <Text strong>{vehicle.vehicleName}</Text><br />
                                <Text type="secondary">Capacity: {vehicle.capacity} passengers</Text>
                            </Card>
                        </Col>
                    ))}
                </Row> */}
                        
                <div className='timeline-container'>
                    <Timeline>
                        {route?.stops?.map((stop, index) => (
                            <Timeline.Item key={index}>
                                <div className="timeline-item">
                                    <div className="timeline-content">
                                        {index === 0 && <Text strong>Initial pick-up</Text>}
                                        {index === route.stops.length - 1 && <Text strong>Final drop-off</Text>}
                                        {index !== 0 && index !== route.stops.length - 1 && <Text strong>Stop {index}</Text>}
                                        <Text type="secondary">{stop.stopName}</Text>
                                        {index === 0 && <Text type="secondary">{dayjs().hour(Math.floor(stop.departSecs / 3600)).minute(Math.floor((stop.departSecs % 3600) / 60)).format('MMM D, YYYY h:mm A')}</Text>}
                                    </div>
                                </div>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                </div>
            </div>
        </Card>
    );
};

export default ReviewStep; 